import type { Fase, Progresso } from '../types/Progresso';

export type Acao =
  | 'carregarAxiis'
  | 'carregarDeNovo'
  | 'revisarTexto'
  | 'editarTexto'
  | 'abrirCampanha'
  | 'enviarConvites'
  | 'enviarNaoRecebidos'
  | 'lembrar'
  | 'baixarPlanilha'
  | 'pausar'
  | 'reabrir'
  | 'fechar'
  | 'editar'
  | 'editarDatas';

export type Regras = {
  primario: Acao | null;
  /** As do `PageHeader`, só na execução; na preparação as ações ficam nos itens (spec 36 R2). */
  secundarias: Acao[];
};

/** Preparação e execução (spec 36 R1). */
export const emPreparacao = (f: Fase) => f === 'preparacao' || f === 'vencida';

/** Pronta para abrir: o bloco de abrir mostra o primário (spec 36 R4). */
export const prontaParaAbrir = (p: Progresso) =>
  p.fase === 'preparacao' && p.naCampanha >= 1 && p.textoRevisado;

/** Spec 36 R8: a tabela, de cima para baixo; vale a primeira linha que casa. */
export function regrasDaFase(p: Progresso): Regras {
  const f = p.fase;
  const planilha: Acao[] = p.responderam >= 1 ? ['baixarPlanilha'] : [];
  // "Carregar de novo" onde a fase permite sincronizar (spec 28 R4); "Texto do convite" sempre.
  const execucao = (extras: Acao[]): Acao[] => [
    ...(f === 'agendada' || f === 'aberta' || f === 'pausada' ? (['carregarDeNovo'] as const) : []),
    ...extras,
    'editarTexto',
    'editar',
  ];

  if (p.rodadaEmAndamento) return { primario: null, secundarias: execucao([]) };
  if (f === 'vencida') return { primario: 'editarDatas', secundarias: [] };
  if (f === 'preparacao') {
    if (p.naCampanha + p.excluidos === 0) return { primario: 'carregarAxiis', secundarias: [] };
    if (p.naCampanha === 0) return { primario: null, secundarias: [] };
    return { primario: p.textoRevisado ? 'abrirCampanha' : 'revisarTexto', secundarias: [] };
  }
  if (f === 'agendada' || f === 'aberta') {
    if (p.naCampanha === 0) return { primario: null, secundarias: execucao(['pausar']) };
    if (p.naoConvidados >= 1)
      return {
        primario: p.convidados + p.falhasConvite === 0 ? 'enviarConvites' : 'enviarNaoRecebidos',
        secundarias: execucao([...planilha, 'pausar']),
      };
    if (f === 'aberta' && p.naoResponderam >= 1)
      return { primario: 'lembrar', secundarias: execucao([...planilha, 'pausar']) };
    if (f === 'aberta') return { primario: 'baixarPlanilha', secundarias: execucao(['pausar']) };
    return { primario: null, secundarias: execucao(['pausar']) };
  }
  if (f === 'encerrada')
    return {
      // Sem resposta a planilha sairia vazia.
      primario: p.responderam >= 1 ? 'baixarPlanilha' : null,
      secundarias: execucao(['fechar']),
    };
  if (f === 'pausada') return { primario: 'reabrir', secundarias: execucao(planilha) };
  return { primario: p.responderam >= 1 ? 'baixarPlanilha' : null, secundarias: execucao([]) };
}

/**
 * `?acao=` que o Início põe na URL ao clicar no primário do cartão (spec 29 R22, spec 36 R13).
 * Só as ações que podem ser o primário.
 */
export const ACAO_NA_URL: Partial<Record<Acao, string>> = {
  carregarAxiis: 'carregar',
  abrirCampanha: 'abrir',
  enviarConvites: 'enviar',
  enviarNaoRecebidos: 'enviarNaoRecebidos',
  lembrar: 'lembrar',
  baixarPlanilha: 'baixar',
  reabrir: 'reabrir',
  editarDatas: 'editarDatas',
};
